<script setup lang="ts">
import {
    VBtn,
    VForm,
    VCard,
    VCardTitle,
    VCardText,
    VCheckbox,
    VTextarea,
    VAutocomplete,
    VTextField,
} from 'vuetify/components';
import { VDateInput } from 'vuetify/labs/VDateInput';
import { requirements } from '@/utils/formRequirements';
import * as zod from 'zod';
import { useI18n } from 'vue-i18n';
import { useField, useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import type { User } from '@/gql/graphql';
import { useLogout } from '@/composables/logout';
import { useFormSession, FormName } from '@/composables/form-session';
import { onBeforeUnmount, watch } from 'vue';

const { t } = useI18n({
    messages: {
        nl: {
            escalation_title: 'Escalatie informatie',
            name: 'Naam',
            description: 'Omschrijving',
            expected_resolution_date: 'Verwachte opleverdatum',
            escalation_reason: 'Verzoek aan verantwoordelijke',
            send_notification: 'Stuur e-mail notificatie',
            solution: 'Verwachte oplossing',
            assigned_user: 'Verantwoordelijke voor de escalatie',
        },
        en: {
            assigned_user: 'Responsible person for the escalation',
            escalation_title: 'Escalation info',
            name: 'Name',
            description: 'Description',
            expected_resolution_date: 'Expected delivery date',
            escalation_reason: 'Request to responsible person',
            send_notification: 'Send email notification',
            solution: 'Expected solution',
        },
    },
});

export interface UpdateEscalationFormValues {
    name: string;
    description: string;
    expectedResolutionDate: Date | null;
    assignedUser?: string;
    solution: string;
    escalationReason: string;
    sendNotification?: boolean;
}

const props = defineProps<{
    initialValues: UpdateEscalationFormValues;
    escalationList: Pick<User, 'fullName' | 'id'>[];
    disabled?: boolean;
    loading?: boolean;
    id: string;
}>();

const emit = defineEmits<{
    cancel: [];
    submit: [values: Required<UpdateEscalationFormValues>];
}>();

const { bottleneck: req } = requirements;

const validationSchema = zod.object({
    name: zod
        .string()
        .min(req.name.min, t('error.required_field'))
        .max(req.name.max, t('error.maximum_characters', { max: req.name.max })),
    description: zod
        .string()
        .min(req.description.min, t('error.required_field'))
        .max(req.description.max, t('error.maximum_characters', { max: req.description.max }))
        .or(zod.literal(null).refine(() => false, t('error.required_field'))),
    expectedResolutionDate: zod
        .date({ required_error: t('error.required_field') })
        .or(zod.literal(null).refine(() => false, t('error.required_field'))),
    solution: zod
        .string()
        .min(req.solution.min, t('error.required_field'))
        .max(
            req.solution.max,
            t('error.maximum_characters', { max: req.solution.max })
        )
        .or(zod.literal(null).refine(() => false, t('error.required_field'))),
    assignedUser: zod.string().min(1),
    escalationReason: zod
        .string()
        .min(req.escalationReason.min, t('error.required_field'))
        .max(
            req.escalationReason.max,
            t('error.maximum_characters', {
                max: req.escalationReason.max,
            })
        )
        .or(zod.literal(null).refine(() => false, t('error.required_field'))),
    sendNotification: zod.boolean().default(false),
});

const { handleSubmit, meta, values, setValues } = useForm({
    validationSchema: toTypedSchema(validationSchema),
    initialValues: props.initialValues,
});

const name = useField<string>('name');
const description = useField<string>('description');
const expectedResolutionDate = useField<Date>('expectedResolutionDate');
const assignedUser = useField<string>('assignedUser');
const solution = useField<string>('solution');
const escalationReason = useField<string>('escalationReason');
const sendNotification = useField<boolean>('sendNotification');

watch(
    () => props.escalationList,
    () => {
        if (assignedUser.value.value) {
            assignedUser.setValue(assignedUser.value.value);
        }
    }
);

const submit = handleSubmit((values) => {
    emit('submit', values as Required<UpdateEscalationFormValues>);
});

const { onBeforeLogout } = useLogout();
const {
    session: formSession,
    createFormSave,
    clearFormSave,
    onResult: onFormSessionResult,
} = useFormSession();

onBeforeLogout(async () => {
    if (!meta.value.dirty) return;

    await createFormSave({
        name: FormName.escalation_edit,
        queryParams: [['id', props.id]],
        fields: {
            ...values,
        },
    });
}, 'form-save');

onFormSessionResult(async () => {
    if (!formSession.value?.name) return;
    if (!formSession.value.fields) return;

    setValues({
        ...formSession.value.fields,
    });
});

onBeforeUnmount(() => {
    clearFormSave();
});

function handleCancel() {
    clearFormSave();
    emit('cancel');
}
</script>

<template>
    <VForm :disabled @submit.prevent="submit">
        <VCard border class="mb-2">
            <VCardTitle>
                <span class="text-label-medium">{{
                    t('escalation_title')
                }}</span></VCardTitle
            >
            <VCardText class="d-flex flex-column ga-3">
                <VTextField
                    v-model="name.value.value"
                    :label="t('name')"
                    :error-messages="name.errorMessage.value"
                />
                <VTextarea
                    v-model="description.value.value"
                    :label="t('description')"
                    :error-messages="description.errorMessage.value"
                    :counter="requirements.bottleneck.description.max"
                    persistent-placeholder
                    rows="5"
                />
                <VDateInput
                    v-model="expectedResolutionDate.value.value"
                    :label="t('expected_resolution_date')"
                    :error-messages="expectedResolutionDate.errorMessage.value"
                    :min="new Date()"
                    placeholder="dd-mm-yyyy"
                    variant="outlined"
                    prepend-icon=""
                    hide-actions
                    clearable
                />
                <VAutocomplete
                    v-model="assignedUser.value.value"
                    :items="escalationList"
                    :label="t('assigned_user')"
                    item-title="fullName"
                    item-value="id"
                    :error-messages="assignedUser.errorMessage.value"
                    :loading
                />
                <VTextarea
                    v-model="solution.value.value"
                    :label="t('solution')"
                    :error-messages="solution.errorMessage.value"
                    :counter="requirements.bottleneck.solution.max"
                    persistent-placeholder
                    rows="5"
                />
                <VTextarea
                    v-model="escalationReason.value.value"
                    :label="t('escalation_reason')"
                    :error-messages="escalationReason.errorMessage.value"
                    :counter="requirements.bottleneck.escalationReason.max"
                    persistent-placeholder
                    rows="5"
                />
                <VCheckbox
                    v-model="sendNotification.value.value"
                    hide-details
                    :label="t('send_notification')"
                />
            </VCardText>
        </VCard>
        <div class="d-flex justify-end ga-3 mt-5">
            <v-btn variant="text" @click="handleCancel">
                {{ t('cancel') }}
            </v-btn>
            <v-btn
                type="submit"
                variant="elevated"
                :disabled="!meta.valid || !meta.dirty"
                :loading
            >
                {{ t('save') }}
            </v-btn>
        </div>
    </VForm>
</template>
